import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import dayjs from "dayjs";
import { useState } from "react";
import { useInterval } from "usehooks-ts";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { EnrichedTurnActivity } from "@/payroll/editor/types";
import { TurnStepHistory, TurnSteps } from "./turn-step-history";

/** Work labels follow saved stages; the timer only updates elapsed time. */
export const TurnActivity = ({
  activity,
  active,
  sending,
  stopping,
}: {
  activity?: EnrichedTurnActivity | null;
  active: boolean;
  sending: boolean;
  stopping: boolean;
}) => {
  const current = sending && !active ? null : activity;
  const ended = current?.ended ?? false;
  const steps = current?.steps ?? [];
  const [now, setNow] = useState(dayjs);
  useInterval(() => setNow(dayjs()), active && !ended ? 30_000 : null);
  if (!active && !sending && (!ended || !steps.length)) return null;
  if (ended) return <TurnStepHistory steps={steps} />;
  const started = current?.startedAt ? dayjs(current.startedAt) : null;
  const minutes = started?.isValid() ? now.diff(started, "minute") : 0;
  const elapsed = active && !ended && minutes > 0 ? ` · ${minutes} min` : "";
  const statusLabel = stopping
    ? "Stopping request…"
    : sending && !active
      ? "Sending request…"
      : (current?.label ?? "Working on your request…");
  const label = `${statusLabel}${elapsed}`;
  const completed = steps.slice(1, 4).reverse();
  return (
    <Stack gap={3} className="min-w-0 py-2 text-sm">
      {!!completed.length && <TurnSteps steps={completed} />}
      <Stack gap={1} role="status" aria-live="polite">
        <Stack direction="row" gap={2} align="center" className="text-subtle">
          {current?.running || sending ? (
            <Spinner aria-hidden className="size-3.5 shrink-0" />
          ) : (
            <ClockIcon aria-hidden className="size-3.5 shrink-0" />
          )}
          <Text variant="body" tone="muted" className="min-w-0">
            {label}
          </Text>
        </Stack>
        {!!current?.detail && (
          <Text variant="body" tone="default" className="pl-6 wrap-anywhere">
            {current.detail}
          </Text>
        )}
      </Stack>
      {steps.length > 4 && <TurnStepHistory steps={steps} />}
    </Stack>
  );
};
